import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Checkbox } from '@/components/ui/Checkbox';
import Button from '@/components/ui/Button';
import StatusBadge from '@/components/ui/StatusBadge';
import { Spinner } from '@/components/ui/Spinner';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/DropdownMenu';
import AppIcon from '@/components/AppIcon';
import Pagination from '@/components/Pagination';
import { appDate } from '@/utils/appDateFormat';

// Define the interface for the employee data THIS COMPONENT EXPECTS
export interface Employee {
  id: number;
  userId: string;
  name: string;
  employeeId: string;
  department: string;
  employmentStatus: string;
  netPay: number;
  payslipStatus: string;
  payslipUrl: string | null;
  basicSalary: number;
  variablePay: number;
  /** Total addition-type adjustments (positive value) */
  adjustmentAdditions?: number;
  /** Total deduction-type adjustments (positive value) */
  adjustmentDeductions?: number;
  /** Net adjustments = adjustmentAdditions - adjustmentDeductions */
  adjustments?: number;
  /**
   * CO-673 — deduction-type ADJUSTMENTS only. Despite the bare name this is NOT the row's total
   * withholding: statutory deductions (pension / GOSI employee shares / CCHI) and tax live on their
   * own fields. Never render this as "Deductions" — use `totalDeductionsOf` below.
   */
  deductions: number;
  /** FDL 33/2021 Art. 19/20/21 — monetized overtime pay, already inside `netPay`. */
  overtimePay?: number;
  /** GPSSA FL 7/1999 / ADPF AD Law 2/2000 — employee pension. Also carries KSA GOSI ANI_emp + SANED_emp. */
  pensionDeduction?: number;
  /** Council of Health Insurance (CCHI) — employee premium share (non-Saudis). */
  cchiEmployeeShare?: number | null;
  /** Income tax withheld, when the tenant generates payslips with tax calculations on. */
  taxAmount?: number;
  payrollDate: string;
  createdOn: string;
  email: string;
  currencyCode?: string;
}

// Define props with default values in the function signature
interface PayslipsSectionProps {
  employees?: Employee[];
  payrollCycles?: string[];
  selectedCycle?: string;
  selectedPayslips?: number[];
  isLoading?: boolean;
  searchTerm?: string;
  currencySymbol?: string;
  currentPage?: number;
  totalCount?: number;
  totalPages?: number;
  pageSize?: number;
  onCycleChange?: (cycle: string) => void;
  onPayslipSelection?: (ids: number[]) => void;
  onPayslipPreview?: (employee: Employee) => void;
  onPayslipDownload?: (employee: Employee) => Promise<void>;
  onPayslipGenerate?: (employee: Employee) => Promise<void>;
  onPayslipRegenerate?: (employee: Employee) => Promise<void>;
  onPayslipNotify?: (employee: Employee) => Promise<void>;
  onCreatePayslip?: (employee: Employee) => Promise<void>;
  onPageChange?: (page: number) => void;
}

/**
 * CO-673 — the row's GROSS EARNINGS: everything the employee earned before anything was withheld.
 *
 * Two differences from what this column used to show:
 *  - it adds `overtimePay`, which the stored `netPay` already includes and the old expression omitted,
 *    so an employee with overtime read Gross < Net;
 *  - it uses `adjustmentAdditions` (the ADDITION side only), not the net `adjustments`
 *    (= additions - deductions). Folding the deduction side into gross nets it back out of
 *    `totalDeductionsOf` below and is exactly how the deduction disappeared from the row.
 *
 * Every term here is money, not hours: `regularOvertimeHours` / `premiumOvertimeHours` are the HOURS
 * and must never be added to a currency total (see the legacy `Overtime` column in the payroll reports,
 * which held hours and was once summed as money).
 */
const grossEarningsOf = (employee: Employee): number =>
  (employee.basicSalary || 0)
  + (employee.overtimePay || 0)
  + (employee.variablePay || 0)
  + (employee.adjustmentAdditions || 0);

/**
 * CO-673 — total withheld for the row: every deduction source the server subtracts to reach net pay.
 *
 * The column used to render `employee.deductions` alone, which is ONLY the deduction-type adjustments
 * total (server: `UserMonthlyPayroll.Deductions`). Statutory withholding never reaches it — the UAE
 * GPSSA / ADPF pension and the KSA GOSI ANI + SANED employee shares are on `pensionDeduction`, the CCHI
 * employee share is on `cchiEmployeeShare`, and tax is on `taxAmount`. That is why a salaried Emirati
 * with a 1,100 pension deduction read Gross 10,000 / Deductions 0 / Net 8,900.
 *
 * The terms below are exactly the ones `UserPayslipService.GeneratePayslipFromPayroll` subtracts:
 *   net = basic + overtime + variable + (addAdj - dedAdj) - deductions - pension - tax - cchi
 * so on a payslip the server actually generated this equals gross - net to the cent. It is written as
 * an explicit sum rather than `gross - net` so that a row whose stored NetPay does NOT agree with its
 * own component fields (dev demo tenants seeded by `PayrollEdgesSeeder`, which writes only the basic
 * component into `BasicSalary` but a full-package figure into `NetPay`) still shows a sane positive
 * total instead of a large negative number.
 *
 * Do NOT add `gosiAniEmployee` / `gosiSanedEmployee` here: the backend has already rolled both into
 * `pensionDeduction` ("PensionDeduction = ANI_emp + SANED_emp", MonthlyPayrollService), so they are
 * breakdown detail, not extra money. Do NOT add `regularOvertimeHours` / `premiumOvertimeHours`
 * anywhere near a currency total either — those are HOURS (the class of bug that once summed the
 * legacy `Overtime` hours column as money in the payroll reports).
 */
const totalDeductionsOf = (employee: Employee): number =>
  (employee.deductions || 0)
  + (employee.adjustmentDeductions || 0)
  + (employee.pensionDeduction || 0)
  + (employee.cchiEmployeeShare || 0)
  + (employee.taxAmount || 0);

const PayslipsSection: React.FC<PayslipsSectionProps> = ({
  // Provide safe default values for EVERY prop
  employees = [],
  payrollCycles = [],
  selectedCycle = '',
  selectedPayslips = [],
  isLoading = false,
  searchTerm: externalSearchTerm,
  currencySymbol = 'AED',
  currentPage = 1,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  totalCount = 0,
  totalPages = 1,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  pageSize = 25,
  onCycleChange = () => {},
  onPayslipSelection = () => {},
  onPayslipPreview = () => {},
  onPayslipDownload = async () => {},
  onPayslipGenerate = async () => {},
  onPayslipRegenerate = async () => {},
  onPayslipNotify = async () => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onCreatePayslip = async () => {},
  onPageChange = () => {},
}) => {
  const { t } = useTranslation();
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [selectedAll, setSelectedAll] = useState(false);

  // CRITICAL SAFETY CHECK: Ensure employees is always an array
  const safeEmployees = Array.isArray(employees) ? employees : [];

  // Use external searchTerm if provided, otherwise use internal
  const effectiveSearch = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;

  // Filter employees based on search term (with safety checks)
  const filteredEmployees = safeEmployees.filter(employee => {
    const name = employee?.name || '';
    const empId = employee?.employeeId || '';
    const dept = employee?.department || '';
    const email = employee?.email || '';
    const term = effectiveSearch.toLowerCase();

    return name.toLowerCase().includes(term) ||
           empId.toLowerCase().includes(term) ||
           dept.toLowerCase().includes(term) ||
           email.toLowerCase().includes(term);
  });

  // Handle select all
  const handleSelectAll = () => {
    if (!selectedAll && safeEmployees.length > 0) {
      const allIds = safeEmployees.map(emp => emp.id).filter(id => id != null);
      onPayslipSelection(allIds);
      setSelectedAll(true);
    } else {
      onPayslipSelection([]);
      setSelectedAll(false);
    }
  };

  // Handle single select
  const handleSelect = (id: number) => {
    const newSelected = selectedPayslips.includes(id)
      ? selectedPayslips.filter(payslipId => payslipId !== id)
      : [...selectedPayslips, id];
    onPayslipSelection(newSelected);
    setSelectedAll(newSelected.length === safeEmployees.length && safeEmployees.length > 0);
  };

  // Get status badge variant
  const getStatusVariant = (status: string): 'success' | 'error' | 'warning' | 'info' | 'neutral' => {
    if (!status) return 'neutral';

    switch (status.toLowerCase()) {
      case 'generated':
        return 'success';
      case 'notified':
      case 'sent':
        return 'info';
      case 'pending':
        return 'warning';
      case 'draft':
        return 'neutral';
      case 'failed':
      case 'error':
        return 'error';
      default:
        return 'neutral';
    }
  };

  // Format currency -- uses per-employee currency code when available
  const formatCurrency = (amount: number, currency?: string) => {
    return `${currency || currencySymbol} ${(amount || 0).toLocaleString()}`;
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return t('N/A');
    try {
      return appDate(dateString) || t('Invalid Date');
    } catch {
      return t('Invalid Date');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px] gap-3">
        <Spinner size="md" />
        <span className="text-sm text-muted-foreground">{t('Loading payslips...')}</span>
      </div>
    );
  }

  return (
    <div>
      {/* Main table */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedAll && safeEmployees.length > 0}
                    indeterminate={selectedPayslips.length > 0 && selectedPayslips.length < safeEmployees.length}
                    onChange={handleSelectAll}
                    disabled={safeEmployees.length === 0}
                  />
                </TableHead>
                <TableHead>{t('Employee')}</TableHead>
                <TableHead>{t('Department')}</TableHead>
                <TableHead>{t('Gross Total')}</TableHead>
                <TableHead>{t('Deductions')}</TableHead>
                <TableHead>{t('Net Pay')}</TableHead>
                <TableHead>{t('Status')}</TableHead>
                <TableHead>{t('Payroll Date')}</TableHead>
                <TableHead className="text-end">{t('Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <p className="text-muted-foreground">
                      {effectiveSearch ? t('No matching employees found') : t('No payslips available')}
                    </p>
                    {effectiveSearch && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setInternalSearchTerm('')}
                        className="mt-2"
                      >
                        {t('Clear search')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map((employee) => (
                  <TableRow
                    key={employee.id}
                    className={selectedPayslips.includes(employee.id) ? 'bg-muted/50' : 'hover:bg-muted/30'}
                  >
                    <TableCell className="w-12">
                      <Checkbox
                        checked={selectedPayslips.includes(employee.id)}
                        onChange={() => handleSelect(employee.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {employee.name || t('Unknown')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {employee.email || t('No email')}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {employee.department || t('N/A')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {formatCurrency(grossEarningsOf(employee), employee.currencyCode)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {formatCurrency(totalDeductionsOf(employee), employee.currencyCode)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium text-foreground">
                        {formatCurrency(employee.netPay || 0, employee.currencyCode)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        data-testid="status-badge"
                        variant={getStatusVariant(employee.payslipStatus)}
                        size="sm"
                      >
                        {employee.payslipStatus || t('Pending')}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {formatDate(employee.payrollDate)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end items-center gap-1">
                        <button
                          title={t('Preview')}
                          className="p-1.5 rounded-md hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          onClick={() => onPayslipPreview(employee)}
                          disabled={isLoading}
                        >
                          <AppIcon name="Eye" size={16} className="text-muted-foreground" />
                        </button>

                        <button
                          title={t('Download PDF')}
                          data-testid="download-btn"
                          className="p-1.5 rounded-md hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
                          onClick={() => onPayslipDownload(employee)}
                          disabled={employee.payslipStatus !== 'Generated' || isLoading}
                        >
                          <AppIcon name="Download" size={16} className="text-muted-foreground" />
                        </button>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              title={t('More actions')}
                              className="p-1.5 rounded-md hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              disabled={isLoading}
                            >
                              <AppIcon name="MoreVertical" size={16} className="text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent sideOffset={4}>
                            {/* Regenerate Payslip */}
                            <DropdownMenuItem
                              onSelect={() => onPayslipRegenerate(employee)}
                            >
                              <AppIcon name="RefreshCw" size={14} className="me-2 text-primary" />
                              {t('Regenerate Payslip')}
                            </DropdownMenuItem>

                            {/* Generate PDF */}
                            <DropdownMenuItem
                              onSelect={() => onPayslipGenerate(employee)}
                              disabled={employee.payslipStatus === 'Generated'}
                            >
                              <AppIcon name="FileText" size={14} className="me-2 text-primary" />
                              {t('Generate PDF')}
                            </DropdownMenuItem>

                            {/* Notify Employee */}
                            <DropdownMenuItem
                              data-testid="notify-btn"
                              onSelect={() => onPayslipNotify(employee)}
                              disabled={employee.payslipStatus !== 'Generated' || employee.payslipStatus === 'Notified'}
                            >
                              <AppIcon name="Bell" size={14} className="me-2 text-primary" />
                              {t('Notify Employee')}
                            </DropdownMenuItem>

                            <div className="border-t border-border my-1" />

                            {/* Download */}
                            <DropdownMenuItem
                              onSelect={() => onPayslipDownload(employee)}
                              disabled={!['Generated', 'Notified'].includes(employee.payslipStatus)}
                            >
                              <AppIcon name="Download" size={14} className="me-2 text-muted-foreground" />
                              {t('Download')}
                            </DropdownMenuItem>

                            {/* Preview */}
                            <DropdownMenuItem
                              onSelect={() => onPayslipPreview(employee)}
                            >
                              <AppIcon name="Eye" size={14} className="me-2 text-muted-foreground" />
                              {t('Preview')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="border-t border-border px-4 py-3" data-testid="pagination">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              variant="compact"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PayslipsSection;
